/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ErrorComponent } from '@msdyn365-commerce-modules/checkout';

import { IGoogleCaptchaVerificationData } from './google-captcha-verification.data';
import { IGoogleCaptchaVerificationProps } from './google-captcha-verification.props.autogenerated';
import { verifyReCaptchaAsync } from '../../actions/extension/DataActionExtension.g';
import { AppInsights } from '../../ApplicationInsights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

interface ICheckoutGuestProfileState {
    hasCheckoutError?: boolean;
    errorMessage?: string;
    isVerified: boolean;
}

export interface ICheckoutGuestProfileModuleProps
    extends IGoogleCaptchaVerificationProps<IGoogleCaptchaVerificationData>,
        IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps
    extends IGoogleCaptchaVerificationProps<IGoogleCaptchaVerificationData>,
        ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    onCaptchaCallback: (token: string) => void;
    byPass: () => void;
    isVerified: boolean;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<IGoogleCaptchaVerificationProps<IGoogleCaptchaVerificationData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        isVerified: false
    };

    public async componentDidMount(): Promise<void> {
        if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
            this.props.moduleState.onReady();
            this.props.moduleState.status = 'ready';
        }
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady }
        } = this.props;
        const { hasCheckoutError, errorMessage } = this.state;

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            onCaptchaCallback: this.onReCaptchaCallback,
            isVerified: this.state.isVerified,
            byPass: this.byPass
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public byPass = () => {
        this.props.moduleState.onReady();
    };

    public onReCaptchaCallback = async (token: string) => {
        verifyReCaptchaAsync({ callerContext: this.props.context.actionContext }, `${token}`)
            .then(async res => {
                if (res.isHuman) {
                    const checkoutState = this.props.data.checkout.result;
                    await checkoutState?.checkoutCart.updateExtensionProperties({
                        newExtensionProperties: [
                            {
                                Key: 'reCaptchaValidation',
                                Value: {
                                    BooleanValue: true
                                }
                            }
                        ]
                    });
                    AppInsights.getInstance(this.props.context).trackTrace({
                        message: `onReCaptchaCallback() - captcha verified`,
                        properties: {
                            file: 'google-captcha-verification',
                            user: this.props.context.request.user,
                            cartId: this.props.data.checkout.result?.checkoutCart.cart.Id,
                            cart: this.props.data.checkout.result?.checkoutCart.cart
                        }
                    });
                    this.props.moduleState.onReady();
                    this.props.moduleState.status = 'ready';
                    this.setState({ isVerified: true });
                } else {
                    this._setErrorMessage('❌ reCAPTCHA server verification failed. Please retry again.');
                    this.setState({ isVerified: false });
                    AppInsights.getInstance(this.props.context).trackTrace({
                        message: `onReCaptchaCallback() - captcha verification failed`,
                        severityLevel: SeverityLevel.Error,
                        properties: {
                            file: 'google-captcha-verification',
                            error: 'Found user is not human',
                            user: this.props.context.request.user,
                            cartId: this.props.data.checkout.result?.checkoutCart.cart.Id,
                            cart: this.props.data.checkout.result?.checkoutCart.cart
                        }
                    });
                }
            })
            .catch(error => {
                this._setErrorMessage('❌ reCAPTCHA server verification failed. Please try again.');
                this.setState({ isVerified: false });
                AppInsights.getInstance(this.props.context).trackTrace({
                    message: `onReCaptchaCallback() - captcha verification failed`,
                    severityLevel: SeverityLevel.Error,
                    properties: {
                        file: 'google-captcha-verification',
                        error: error,
                        user: this.props.context.request.user,
                        cartId: this.props.data.checkout.result?.checkoutCart.cart.Id,
                        cart: this.props.data.checkout.result?.checkoutCart.cart
                    }
                });
            });
    };

    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            errorMessage,
            hasCheckoutError: true
        });
    };
}

export default CheckoutGuestProfile;
